import React, {useEffect, useState} from 'react';
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Message} from "primereact/message";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {InputSwitch} from "primereact/inputswitch";
import {InputText} from "primereact/inputtext";
import {Tag} from "primereact/tag";
import {MultiSelect} from "primereact/multiselect";
import {Dropdown} from "primereact/dropdown";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import WebSearchSetting from "../Admin/WebSearchSetting";
import CampaignJobsState from "./CampaignJobsState";
import CampaignSearchSummary from "./CampaignSearchSummary";
import CampaignSiteSearchSummary from "./CampaignSiteSearchSummary";
import {CampaignService} from "../../service/CampaignService";
import {ScraperService} from "../../service/ScraperService";
import {search_fields} from "../../utils/Constants";
import {normalizeHostSearchConfig} from "../Admin/JobSchedulingSiteSearchSetting";

// Builds the host record written into
// campaign.settings.site_search_job.search_fields_based_url[url], always
// through the shared normalizer so a `fields` list is guaranteed and any
// unrelated key already on the record survives. `target` is only written
// when `target` is not `undefined` (the caller passes `undefined` whenever
// the supplier module is off) so a campaign that never uses the module gets
// no new `target` key at all -- an omitted `target` here is NOT the same as
// 'competitor': the latter is an explicit, persisted choice, the former means
// this call never wrote the field. When `target` IS passed, an empty/falsy
// value still resolves to the explicit 'competitor' string (never omitted).
export const buildSiteSearchHostRecord = (existing, fields, target) => {
    const base = {
        ...normalizeHostSearchConfig(existing),
        selected: true,
        fields,
    };
    return target === undefined ? base : {...base, target: target || 'competitor'};
};

// Filters a site list by case-insensitive substring match against `url`. An
// empty/whitespace filter returns every site -- the dialog's default view.
export const filterSitesByUrl = (sites, filterText) => {
    const needle = (filterText || '').trim().toLowerCase();
    if (!needle) return sites;
    return sites.filter(s => (s.url || '').toLowerCase().includes(needle));
};

// The ready-sites table is fed a filtered `value`, so PrimeReact's
// onSelectionChange only ever knows about rows currently visible under the
// filter: the header "select all"/"deselect all" checkbox replaces the WHOLE
// selection with just what's rendered (see PrimeReact DataTable's
// onHeaderCheckboxClick, which rebuilds selection from `value` alone), so a
// naive `setSiteSearchSelection(e.value)` silently drops anything selected
// while a different filter was active.
//
// Reconciles PrimeReact's reported selection (`nextSelection`, computed only
// from `visibleRows`) with whatever was already selected under a different
// filter (`prevSelection`): rows hidden by the current filter are always
// carried over untouched, rows still visible follow exactly what PrimeReact
// just reported (ticked stays in, unticked drops out). Deduped by url so
// this stays correct even for a single-row toggle, where PrimeReact's own
// event already carries the full selection (hidden rows included).
export const mergeSiteSearchSelection = (prevSelection, visibleRows, nextSelection) => {
    const visibleUrls = new Set((visibleRows || []).map(r => r.url));
    const next = nextSelection || [];
    const nextUrls = new Set(next.map(s => s.url));
    const hiddenSelected = (prevSelection || [])
        .filter(s => !visibleUrls.has(s.url) && !nextUrls.has(s.url));
    return [...next, ...hiddenSelected];
};

const campaignService = new CampaignService();
const scraperService = new ScraperService();

// Google web-search competitor suggestion: searches Google per product and
// collects competitor suggestions, driven by campaign.settings.competitor_suggestion.
const SEARCH_TYPE = 'competitor_suggestion';
// On-site search job type (one request per site, reads site_search_job settings).
const SITE_SEARCH_TYPE = 'site_search_campaign';

const DEFAULT_SEARCH_SETTINGS = {
    selected_fields: [{name: 'Name', code: 'name'}],
    page_number: 3,
    google_shopping: false,
    is_searching_enabled: true,
    is_site_searching_enabled: true,
    also_find_new_websites: false,
    exclude_products_with_suggestions: false,
    seller_filter: 'no_seller_filter',
    competitor_urls: [],
    excluded_competitor_urls: [
        'https://facebook.com', 'https://instagram.com', 'https://youtube.com', 'https://pinterest.com',
        'https://wikipedia.org', 'https://reddit.com', 'https://twitter.com', 'https://github.com',
    ],
    job_units: 1,
    job_priority: 3,
    use_headless: false,
    divided_competitors_no: 0,
};

const SearchOnboardingStep = ({t, status, refresh}) => {
    const {toast, setError} = React.useContext(AppContext);
    const {selectedCampaign, loadCampaigns} = React.useContext(CampaignContext);
    const campaignId = selectedCampaign?.id;

    const [showDialog, setShowDialog] = useState(false);
    const [running, setRunning] = useState(false);
    // Bumping this reloads the shared "Job State" table after a run.
    const [jobsReload, setJobsReload] = useState(0);
    const [competitorSites, setCompetitorSites] = useState([]);
    const [selectedSites, setSelectedSites] = useState([]);

    // Live-edited settings (WebSearchSetting mutates in place), seeded from the
    // campaign's existing competitor_suggestion config.
    const [searchSettings] = useState(() => {
        const cs = (selectedCampaign?.settings && selectedCampaign.settings.competitor_suggestion) || {};
        return {
            ...DEFAULT_SEARCH_SETTINGS,
            ...cs,
            is_searching_enabled: cs.is_searching_enabled !== undefined ? cs.is_searching_enabled : true,
            selected_fields: (cs.selected_fields && cs.selected_fields.length) ? cs.selected_fields : DEFAULT_SEARCH_SETTINGS.selected_fields,
            page_number: cs.page_number || DEFAULT_SEARCH_SETTINGS.page_number,
        };
    });

    const [siteSearching, setSiteSearching] = useState(false);
    const [showSiteSearchDialog, setShowSiteSearchDialog] = useState(false);
    const [siteSearchSelection, setSiteSearchSelection] = useState([]);
    // Narrows both site-search tables by a case-insensitive substring of the
    // url -- 58 ready sites in a 10-row paginator otherwise takes six pages
    // to search by hand, and the browser's own find can't reach a hidden page.
    const [siteSearchFilter, setSiteSearchFilter] = useState('');
    // Per-site search fields (url -> [field codes]) the on-site search builds its
    // keyword from. Seeded from any existing per-URL config; default empty.
    const [siteSearchFieldsByUrl, setSiteSearchFieldsByUrl] = useState(() => {
        const job = (selectedCampaign?.settings && selectedCampaign.settings.site_search_job) || {};
        const map = job.search_fields_based_url || {};
        const out = {};
        Object.keys(map).forEach(url => {
            out[url] = (map[url] && map[url].fields ? map[url].fields : []).map(f => f.code);
        });
        return out;
    });

    const setFieldsForUrl = (url, codes) => setSiteSearchFieldsByUrl(prev => ({...prev, [url]: codes}));

    // Per-site target (competitor | supplier), gated behind the supplier
    // module. Seeded from any existing per-URL config; default 'competitor'.
    const [siteSearchTargetByUrl, setSiteSearchTargetByUrl] = useState(() => {
        const job = (selectedCampaign?.settings && selectedCampaign.settings.site_search_job) || {};
        const map = job.search_fields_based_url || {};
        const out = {};
        Object.keys(map).forEach(url => {
            out[url] = (map[url] && map[url].target) || 'competitor';
        });
        return out;
    });
    const setTargetForUrl = (url, target) => setSiteSearchTargetByUrl(prev => ({...prev, [url]: target}));
    const targetOptions = [
        {value: 'competitor', label: t('suggestion.target_competitor')},
        {value: 'supplier', label: t('suggestion.target_supplier')}
    ];
    const isSupplierModuleActive = !!selectedCampaign?.settings?.supplier_module?.is_active;
    const [excludedSites, setExcludedSites] = useState(
        (searchSettings.excluded_competitor_urls || []).map(u => ({url: u}))
    );

    // Per-site on-site search (site-içi arama): searches the site's internal
    // search for the campaign's products. Only available when the site is ready.
    // Persist the chosen search fields per selected site so the on-site search
    // spider (site_search_campaign) builds its keyword from them.
    // Spider reads campaign.settings.site_search_job.search_fields_based_url[url].fields.
    const persistSiteSearchFields = (sites) => {
        const settings = {...(selectedCampaign.settings || {})};
        const job = {...(settings.site_search_job || {})};
        const map = {...(job.search_fields_based_url || {})};
        sites.forEach(s => {
            const codes = siteSearchFieldsByUrl[s.url] || [];
            // Keep the order in which the user ticked the fields: the search
            // keyword is built by concatenating them in this exact order.
            const fields = codes.map(code => {
                const found = search_fields.find(f => f.code === code);
                return found ? {code: found.code, name: found.name} : {code};
            });
            const target = isSupplierModuleActive ? (siteSearchTargetByUrl[s.url] || 'competitor') : undefined;
            map[s.url] = buildSiteSearchHostRecord(map[s.url], fields, target);
        });
        job.search_fields_based_url = map;
        settings.site_search_job = job;
        return campaignService.saveCampaignSettings(campaignId, selectedCampaign.use_sale_value, settings)
            .then(() => {
                selectedCampaign.settings = settings;
                if (loadCampaigns) loadCampaigns();
            });
    };

    // Bulk: start on-site search for all selected sites that are ready, at once.
    const runSiteSearchSelected = () => {
        const ready = siteSearchSelection.filter(s => s.is_site_searching_ready);
        if (ready.length === 0)
            return;
        setSiteSearching(true);
        persistSiteSearchFields(ready)
            .then(() => Promise.all(ready.map(s =>
                scraperService.saveScrapingRequest({type: 'site_search_campaign', campaign_id: campaignId, url: s.url}))))
            .then(() => {
                setShowSiteSearchDialog(false);
                toast.current.show({
                    severity: 'success',
                    summary: t('onboarding.search.siteSearchStarted'),
                    detail: t('onboarding.search.siteSearchStartedCount').replace('{n}', ready.length),
                });
                if (refresh) refresh();
                setJobsReload(v => v + 1);
            })
            .catch(fail)
            .finally(() => setSiteSearching(false));
    };

    const isHostExcluded = (row) => excludedSites.some(s => s.url === row.url);
    const toggleHostExcluded = (row) => {
        const next = isHostExcluded(row)
            ? excludedSites.filter(s => s.url !== row.url)
            : [...excludedSites, {url: row.url}];
        setExcludedSites(next);
        searchSettings.excluded_competitor_urls = next.map(s => s.url);
    };

    useEffect(() => {
        if (!campaignId) return;
        campaignService.getCampaignCompetitors(campaignId).then(rows => {
            const list = rows || [];
            setCompetitorSites(list);
            // Pre-select: previously configured competitor_urls, else all sites.
            const configured = searchSettings.competitor_urls && searchSettings.competitor_urls.length
                ? list.filter(s => searchSettings.competitor_urls.includes(s.url))
                : list;
            setSelectedSites(configured);
            searchSettings.competitor_urls = configured.map(s => s.url);
        }).catch(() => {});
    }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

    const fail = (error) => {
        setError(error);
        setError(null);
        setRunning(false);
    };

    const persistSettings = () => {
        const merged = {...(selectedCampaign.settings || {}), competitor_suggestion: {...searchSettings}};
        return campaignService.saveCampaignSettings(campaignId, selectedCampaign.use_sale_value, merged)
            .then(() => {
                selectedCampaign.settings = merged;
                if (loadCampaigns) loadCampaigns();
            });
    };

    // Popup "Save" -> persist the google-search setting without creating a job.
    const saveSettings = () => {
        setRunning(true);
        persistSettings()
            .then(() => {
                setShowDialog(false);
                toast.current.show({
                    severity: 'success',
                    summary: t('onboarding.search.settingsSaved'),
                    detail: t('onboarding.search.settingsSavedHint'),
                });
            })
            .then(() => setRunning(false))
            .catch(fail);
    };

    // Popup "Run" -> save the google-search setting + create the scraping job.
    const runSearch = () => {
        setRunning(true);
        persistSettings()
            .then(() => scraperService.saveScrapingRequest({type: SEARCH_TYPE, url: '', campaign_id: campaignId}))
            .then(() => {
                setShowDialog(false);
                toast.current.show({
                    severity: 'success',
                    summary: t('onboarding.search.started'),
                    detail: t('onboarding.search.startedHint'),
                });
                if (refresh) refresh();
                setJobsReload(v => v + 1);
            })
            .then(() => setRunning(false))
            .catch(fail);
    };

    return (
        <div>
            <h2 style={{margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 10}}>
                <i className="pi pi-search"/> {t('onboarding.step.search.label', 'Tarama')}
            </h2>
            <p style={{color: '#6b7280', maxWidth: 640, marginTop: 0}}>{t('onboarding.step.search.desc')}</p>

            {/* Two actions: Google search + on-site search, each with its last status. */}
            <div style={{display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8}}>
                <div style={{flex: '1 1 280px', minWidth: 260, border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <i className="pi pi-search" style={{color: '#d97706'}}/> {t('onboarding.search.engineSearch')}
                    </div>
                    <p style={{color: '#6b7280', fontSize: '.85rem', margin: '0 0 12px'}}>{t('onboarding.search.startedHint')}</p>
                    <Button label={t('onboarding.search.engineSearch')} icon="pi pi-search"
                            className="p-button-warning" onClick={() => setShowDialog(true)}/>
                </div>

                <div style={{flex: '1 1 280px', minWidth: 260, border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <i className="pi pi-search-plus" style={{color: '#2563eb'}}/> {t('onboarding.search.siteSearch')}
                    </div>
                    <p style={{color: '#6b7280', fontSize: '.85rem', margin: '0 0 12px'}}>{t('onboarding.search.siteSearchPopupHint')}</p>
                    <Button label={t('onboarding.search.startSiteSearch')} icon="pi pi-search-plus"
                            onClick={() => setShowSiteSearchDialog(true)}/>
                </div>
            </div>

            {/* Shared Job State: last 3 days of both job types for this campaign. */}
            <div style={{marginTop: 20, border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                <CampaignJobsState campaignId={campaignId}
                                   types={[SEARCH_TYPE, SITE_SEARCH_TYPE]} days={2}
                                   reloadToken={jobsReload}/>
                <CampaignSearchSummary campaignId={campaignId} reloadToken={jobsReload}/>
                <CampaignSiteSearchSummary campaignId={campaignId} reloadToken={jobsReload}/>
            </div>

            {/* Popup: Google search settings + seller sites (drives seller_filter). */}
            <Dialog header={t('onboarding.search.engineSearch')} visible={showDialog}
                    style={{width: '680px', maxWidth: '95vw'}} modal onHide={() => setShowDialog(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text"
                                    onClick={() => setShowDialog(false)}/>
                            <Button label={t('actions.save')} icon="pi pi-save"
                                    className="p-button-secondary" onClick={saveSettings} disabled={running}/>
                            <Button label={t('onboarding.sites.run')} icon="pi pi-play"
                                    onClick={runSearch} disabled={running}/>
                        </div>
                    }>
                <WebSearchSetting webSearchSetting={searchSettings}/>

                <div style={{marginTop: "1em"}}>
                    <b>{t('onboarding.search.sellerSites')}</b>
                    <small style={{display: "block", color: "#6b7280", marginBottom: ".5em"}}>
                        {t('onboarding.search.sellerSitesHint')}
                    </small>
                    {competitorSites.length === 0
                        ? <Message severity="info" text={t('onboarding.sites.empty')}/>
                        : <DataTable value={competitorSites} dataKey="url" responsiveLayout="scroll"
                                     selection={selectedSites} selectionMode="checkbox"
                                     paginator={competitorSites.length > 8} rows={8}
                                     onSelectionChange={(e) => {
                                         setSelectedSites(e.value);
                                         searchSettings.competitor_urls = e.value.map(s => s.url);
                                     }}>
                            <Column selectionMode="multiple" headerStyle={{width: '3em'}}/>
                            <Column header={t('onboarding.sites.site')}
                                    body={(row) => <a href={row.url} target="_blank" rel="noopener noreferrer"
                                                      onClick={(ev) => ev.stopPropagation()}>{row.url}</a>}/>
                            <Column header={t('onboarding.search.suggestFromHost')} style={{width: '8em'}}
                                    body={(row) =>
                                        <span onClick={(ev) => ev.stopPropagation()} onMouseDown={(ev) => ev.stopPropagation()}>
                                            <InputSwitch checked={!isHostExcluded(row)} onChange={() => toggleHostExcluded(row)}/>
                                        </span>}/>
                          </DataTable>}
                </div>
            </Dialog>

            {/* On-site search popup: pick ready sites + per-site fields, start. */}
            <Dialog header={t('onboarding.search.siteSearch')} visible={showSiteSearchDialog}
                    style={{width: '680px', maxWidth: '95vw'}} modal onHide={() => !siteSearching && setShowSiteSearchDialog(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text"
                                    disabled={siteSearching} onClick={() => setShowSiteSearchDialog(false)}/>
                            <Button label={t('onboarding.sites.run') + (siteSearchSelection.filter(s => s.is_site_searching_ready).length ? ` (${siteSearchSelection.filter(s => s.is_site_searching_ready).length})` : '')}
                                    icon="pi pi-play" loading={siteSearching}
                                    disabled={siteSearching || siteSearchSelection.filter(s => s.is_site_searching_ready).length === 0}
                                    onClick={runSiteSearchSelected}/>
                        </div>
                    }>
                <small style={{display: "block", color: "#6b7280", marginBottom: ".5em"}}>
                    {t('onboarding.search.siteSearchPopupHint')} {t('onboarding.search.searchFieldsHint')}
                </small>
                <span className="p-input-icon-left" style={{display: 'block', marginBottom: '.75em'}}>
                    <i className="pi pi-search"/>
                    <InputText value={siteSearchFilter} style={{width: '100%'}}
                               placeholder={t('onboarding.search.siteSearchFilterPlaceholder')}
                               onChange={(e) => setSiteSearchFilter(e.target.value)}/>
                </span>
                {(() => {
                    const ready = competitorSites.filter(s => s.is_site_searching_ready);
                    const notReady = competitorSites.filter(s => !s.is_site_searching_ready);
                    const filteredReady = filterSitesByUrl(ready, siteSearchFilter);
                    const filteredNotReady = filterSitesByUrl(notReady, siteSearchFilter);
                    const readyCount = filteredReady.length === ready.length
                        ? `${ready.length}` : `${filteredReady.length} / ${ready.length}`;
                    const notReadyCount = filteredNotReady.length === notReady.length
                        ? `${notReady.length}` : `${filteredNotReady.length} / ${notReady.length}`;
                    return <div>
                        <b>{t('onboarding.search.siteSearchReadyTitle')} ({readyCount})</b>
                        {ready.length === 0
                            ? <Message severity="info" text={t('onboarding.search.siteSearchNoReady')} style={{display: 'block', marginTop: '.4em'}}/>
                            : <DataTable value={filteredReady} dataKey="url" responsiveLayout="scroll" style={{marginTop: '.4em'}}
                                         selection={siteSearchSelection} selectionMode="checkbox"
                                         onSelectionChange={(e) => setSiteSearchSelection(prev =>
                                             mergeSiteSearchSelection(prev, filteredReady, e.value || []))}
                                         emptyMessage={t('onboarding.search.siteSearchFilterNoMatch')}
                                         paginator={filteredReady.length > 10} rows={10}>
                                <Column selectionMode="multiple" headerStyle={{width: '3em'}}/>
                                <Column header={t('onboarding.sites.site')}
                                        body={(row) => <a href={row.url} target="_blank" rel="noopener noreferrer"
                                                          onClick={(ev) => ev.stopPropagation()}>{row.url}</a>}/>
                                <Column header={t('onboarding.search.searchFields')} style={{width: '16em'}}
                                        body={(row) =>
                                            <span onClick={(ev) => ev.stopPropagation()} onMouseDown={(ev) => ev.stopPropagation()}>
                                                <MultiSelect value={siteSearchFieldsByUrl[row.url] || []} options={search_fields}
                                                             optionLabel="name" optionValue="code" display="chip" filter
                                                             placeholder={t('onboarding.search.searchFields')}
                                                             style={{width: '100%'}}
                                                             onChange={(e) => setFieldsForUrl(row.url, e.value || [])}/>
                                            </span>}/>
                                {isSupplierModuleActive &&
                                    <Column header={t('suggestion.target')} style={{width: '10em'}}
                                            body={(row) =>
                                                <span onClick={(ev) => ev.stopPropagation()} onMouseDown={(ev) => ev.stopPropagation()}>
                                                    <Dropdown value={siteSearchTargetByUrl[row.url] || 'competitor'} options={targetOptions}
                                                              onChange={(e) => setTargetForUrl(row.url, e.value)}/>
                                                </span>}/>}
                              </DataTable>}

                        {notReady.length > 0 && <div style={{marginTop: '1em'}}>
                            <b style={{color: '#6b7280'}}>{t('onboarding.search.siteSearchNotReadyTitle')} ({notReadyCount})</b>
                            <DataTable value={filteredNotReady} dataKey="url" responsiveLayout="scroll" style={{marginTop: '.4em', opacity: 0.7}}
                                       emptyMessage={t('onboarding.search.siteSearchFilterNoMatch')}
                                       paginator={filteredNotReady.length > 8} rows={8}>
                                <Column header={t('onboarding.sites.site')}
                                        body={(row) => <a href={row.url} target="_blank" rel="noopener noreferrer">{row.url}</a>}/>
                                <Column header={t('onboarding.search.siteSearchReady')} style={{width: '12em'}}
                                        body={() => <Tag value={t('onboarding.sites.needsSearch')} severity="warning"/>}/>
                            </DataTable>
                        </div>}
                    </div>;
                })()}
            </Dialog>

        </div>
    );
};

export default SearchOnboardingStep;
